import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ConsolidatedContact,
  RecommendedContactStatusOptionsObject,
} from '../../../../api/CoachingNotes/schema';
import { CONSOLIDATED_CONTACT_CATEGORY } from '../../../../Util/GlobalConstants';
import CoachingEmptyState from '../../../../Assets/coachingEmptyState.png';

export const RECOMMEND_SIZE = 15;
export const RECOMMEND_SIZE_WITH_SA = 40;
export const RECOMMEND_KUDO_COUNT = 5;
export const RECOMMEND_KUDO_COUNT_WITH_SA = 10;
export const RECOMMEND_CONCESSION_COUNT = 5;
export const PAGINATION_SIZE_WITH_SA_RECOMMENDED = 15; // Used in Recommended tab
export const PAGINATION_SIZE = 10; // Used in other tabs like Recent
export interface TabProps {
  contacts: ConsolidatedContact[];
  username: string;
  userID: string;
  refreshContacts?: () => void;
  sourcePage?: string;
  isTm?: boolean;
}

export interface ActionStatus {
  action: string;
  status: string;
  date: string;
}

export enum SortableColumn {
  CONTACT_ID = 'contactID',
  MARKETPLACE_ID = 'marketplaceID',
  CONTACT_DATE = 'contactDate',
  SIC = 'sic',
  SKILL = 'skill',
  CATEGORY = 'category',
  OWNERSHIP = 'ownership',
  DURATION = 'duration',
  CHANNEL = 'channel',
  STATUS = 'status',
  LAST_MODIFIED_DATE = 'lastModifiedDate',
  LAST_MODIFIED_USER = 'lastModifiedUser',
  INIT = 'init',
}

export enum SortableDirection {
  ASCENDING = 'ascending',
  DECENDING = 'decending',
}

export const compareTwoArray = (
  arr1: string | null,
  arr2: string | null,
  sortDirection: string,
  sortCol: SortableColumn
): number => {
  if (arr1 === null && arr2 === null) {
    return 0;
  }
  if (arr1 === null && arr2 !== null) {
    return sortDirection === 'ascending' ? -1 : 1;
  }
  if (arr1 !== null && arr2 === null) {
    return sortDirection === 'ascending' ? 1 : -1;
  }
  if (arr1 !== null && arr2 !== null) {
    const arr1Len = arr1.length;
    const arr2Len = arr2.length;
    if (sortCol === SortableColumn.DURATION) {
      if (parseInt(arr1) < parseInt(arr2)) {
        return sortDirection === 'ascending' ? -1 : 1;
      } else if (parseInt(arr1) > parseInt(arr2)) {
        return sortDirection === 'ascending' ? 1 : -1;
      }
    }
    let arr1Index = 0;
    let arr2Index = 0;

    while (arr1Index < arr1Len && arr2Index < arr2Len) {
      if (arr1[arr1Index] === arr2[arr2Index]) {
        arr1Index += 1;
        arr2Index += 1;
      } else {
        if (arr1[arr1Index] > arr2[arr2Index])
          return sortDirection === 'ascending' ? 1 : -1;
        if (arr1[arr1Index] < arr2[arr2Index])
          return sortDirection === 'ascending' ? -1 : 1;
      }
    }

    if (arr1Index === arr1Len && arr2Index === arr2Len) return 0;

    return arr1Index === arr1Len && sortDirection === 'ascending' ? -1 : 1;
  }
  return 0;
};

export const customSort = (
  sortCol: SortableColumn,
  sortDir: SortableDirection,
  noteA: any,
  noteB: any
): number => {
  const sortColumn = sortCol as string;
  const sortDirection = sortDir as string;

  if (sortColumn !== SortableColumn.INIT) {
    // sort by selected column
    let a = noteA[sortColumn];
    let b = noteB[sortColumn];

    if (
      sortColumn === SortableColumn.LAST_MODIFIED_USER ||
      sortColumn === SortableColumn.LAST_MODIFIED_DATE
    ) {
      a = noteA.contactInfo[sortColumn];
      b = noteB.contactInfo[sortColumn];
    }

    if (
      sortColumn === SortableColumn.CATEGORY ||
      sortColumn === SortableColumn.SIC ||
      sortColumn === SortableColumn.OWNERSHIP ||
      sortColumn === SortableColumn.DURATION ||
      sortColumn === SortableColumn.CHANNEL ||
      sortColumn === SortableColumn.LAST_MODIFIED_USER ||
      sortColumn === SortableColumn.LAST_MODIFIED_DATE
    ) {
      return compareTwoArray(a, b, sortDirection, sortColumn);
    }

    if (a < b) {
      return sortDirection === 'ascending' ? -1 : 1;
    } else if (a > b) {
      return sortDirection === 'ascending' ? 1 : -1;
    }
  } else {
    // initial order
    if (
      noteA.status === RecommendedContactStatusOptionsObject.REVIEWED &&
      noteB.status === RecommendedContactStatusOptionsObject.REVIEWED
    ) {
      // compare by date then display order
      if (
        noteA.calWeek === noteB.calWeek &&
        noteA.calYear === noteB.calYear &&
        noteA.displayOrder &&
        noteB.displayOrder
      ) {
        // compare displayoder ascending
        return noteA.displayOrder < noteB.displayOrder ? -1 : 1;
      }
      // else cal week and cal year are diff
      if (noteA.calYear === noteB.calYear) {
        // compare cal week descending
        return noteA.calWeek < noteB.calWeek ? 1 : -1;
      }
      // compare calyear descending
      return noteA.calYear < noteB.calYear ? 1 : -1;
    }

    // Display concession compliance first among other categories
    if (
      noteA.category === CONSOLIDATED_CONTACT_CATEGORY.CONCESSION_COMPLIANCE &&
      noteB.category !== CONSOLIDATED_CONTACT_CATEGORY.CONCESSION_COMPLIANCE
    ) {
      return -1;
    } else if (
      noteA.category !== CONSOLIDATED_CONTACT_CATEGORY.CONCESSION_COMPLIANCE &&
      noteB.category === CONSOLIDATED_CONTACT_CATEGORY.CONCESSION_COMPLIANCE
    ) {
      return 1;
    }

    if (
      noteA.calWeek === noteB.calWeek &&
      noteA.calYear === noteB.calYear &&
      noteA.displayOrder &&
      noteB.displayOrder
    ) {
      // compare displayoder ascending
      return noteA.displayOrder < noteB.displayOrder ? -1 : 1;
    }

    // else cal week and cal year are diff
    if (noteA.calYear === noteB.calYear) {
      // compare cal week descending
      return noteA.calWeek < noteB.calWeek ? 1 : -1;
    }

    // compare calyear descending
    return noteA.calYear < noteB.calYear ? 1 : -1;
  }

  return 0;
};

export const mapToActionStatus = (
  contact: ConsolidatedContact
): ActionStatus => {
  let action = '';
  let status = '';
  let date = '';

  if (contact.status === RecommendedContactStatusOptionsObject.NOTREVIEWED) {
    action = 'Review';
    status = 'Not Reviewed';
  } else if (
    contact.status === RecommendedContactStatusOptionsObject.REVIEWED
  ) {
    action = 'Edit';
    status = 'Reviewed';
  } else if (
    contact.status === RecommendedContactStatusOptionsObject.COACHABLE
  ) {
    action = 'Edit';
    status = 'Coachable';
  } else if (
    contact.status === RecommendedContactStatusOptionsObject.PENDINGCOACHING
  ) {
    action = 'View';
    status = 'Pending Coaching';
  } else if (contact.status === RecommendedContactStatusOptionsObject.COACHED) {
    action = 'View';
    status = 'Coached';
    if (contact.deliverInfo?.deliverDate) {
      date = moment(contact.deliverInfo?.deliverDate).format('YYYY-MM-DD');
    }
  } else if (
    contact.status === RecommendedContactStatusOptionsObject.UNCOACHABLE
  ) {
    action = 'Edit';
    status = 'Uncoachable';
  }

  return { action, status, date } as ActionStatus;
};

export interface StatusTextProps {
  status: string;
  date: string;
  'data-*'?: string;
}

export const StatusText: React.FC<StatusTextProps> = ({
  status,
  date,
  ...props
}: StatusTextProps): React.ReactElement => {
  return (
    <Text {...props}>
      {status === 'Coached' && (
        <>
          <FormattedMessage
            id='cs_perfportal_contact_recom_coached_status'
            defaultMessage='Coached '
            description='coached_status'
          />{' '}
          <FormattedMessage
            id='cs_perfportal_rec_coached_date'
            defaultMessage='(delivered {0})'
            description='coached_date'
            values={{ 0: date }}
          />
        </>
      )}
      {status === 'Reviewed' && (
        <FormattedMessage
          id='cs_perfportal_contact_recom_reviewed_status'
          defaultMessage='Reviewed'
          {...props}
        />
      )}
      {status === 'Coachable' && (
        <FormattedMessage
          id='cs_perfportal_contact_recom_coachable_status'
          defaultMessage='Coachable'
          description='coachable_status'
        />
      )}
      {status === 'Uncoachable' && (
        <FormattedMessage
          id='cs_perfportal_contact_recom_uncoachable_status'
          defaultMessage='Uncoachable'
          description='uncoachable_status'
        />
      )}
      {status === 'Pending Coaching' && (
        <FormattedMessage
          id='cs_perfportal_contact_recom_pendingcoaching_status'
          defaultMessage='Pending coaching'
          description='pendingcoaching_status'
        />
      )}
      {status === 'Not Reviewed' && (
        <FormattedMessage
          id='cs_perfportal_contact_recom_notreviewed_status'
          defaultMessage='Not Reviewed'
          description='notreviewed_status'
        />
      )}
    </Text>
  );
};

export const NoContactView: React.FC<{}> = (): React.ReactElement => {
  return (
    <Column width='100%' alignmentHorizontal='center' spacing='300'>
      <div>
        <img src={CoachingEmptyState} alt='no contacts' />
      </div>
      <Row>
        <Text>
          <FormattedMessage
            id='cs_perfportal_contact_recom_noContact'
            defaultMessage='There are no contacts to display here at the moment.'
          />
        </Text>
      </Row>
    </Column>
  );
};

export const EmptyFilterView: React.FC<{}> = (): React.ReactElement => {
  return (
    <Column width='100%' alignmentHorizontal='center' spacing='300'>
      <div>
        <img src={CoachingEmptyState} alt='no contacts' />
      </div>
      <Row>
        <Text>
          <FormattedMessage
            id='cs_perfportal_contact_recom_empty_filter'
            defaultMessage='There are no contacts matching the search criteria.'
          />
        </Text>
      </Row>
    </Column>
  );
};

const ownership2key: Record<string, string> = {
  'marked spam': 'cs_perfportal_ownership_marked_spam',
  'marked duplicate': 'cs_perfportal_ownership_marked_duplicate',
  consulted: 'cs_perfportal_ownership_consulted',
  transferred: 'cs_perfportal_ownership_transferred',
  'chat dropped': 'cs_perfportal_ownership_chat_dropped',
  parked: 'cs_perfportal_ownership_parked',
  resolved: 'cs_perfportal_ownership_resolved',
  unknown: 'cs_perfportal_ownership_unknown',
  unresolved: 'cs_perfportal_ownership_unresolved',
};

const channel2key: Record<string, string> = {
  chat: 'cs_perfportal_channel_chat',
  email: 'cs_perfportal_channel_email',
  fax: 'cs_perfportal_channel_fax',
  'message us': 'cs_perfportal_channel_message_us',
  phone: 'cs_perfportal_channel_phone',
  'postal mail': 'cs_perfportal_channel_postal_mail',
  workitem: 'cs_perfportal_channel_workitem',
};

const contactStatus2Key: Record<string, string> = {
  draft: 'cs_perfportal_coaching_status_not_coached',
  delivered: 'cs_perfportal_status_delivered',
  reviewed: 'cs_perfportal_contact_recom_reviewed_status',
};

export const NOT_FOUND_KEY = ' ';
export const NOT_FOUND_LABEL = ' ';

export const getOwnershipI18nKey = (ownershipValue: string): string => {
  return ownership2key[ownershipValue.toLowerCase()] ?? NOT_FOUND_KEY;
};

export const getChannelI18nKey = (channelValue: string): string => {
  return channel2key[channelValue.toLowerCase()] ?? NOT_FOUND_KEY;
};

export const formatDuration = (secondsStr: string): string => {
  const total = parseInt(secondsStr);
  if (isNaN(total)) return '';

  const mins = Math.floor(total / 60);
  const secs = total % 60;
  const paddedSecs = secs < 10 ? `0${secs}` : `${secs}`;
  return `${mins}:${paddedSecs} min`;
  // 'min' is not part of i18n. Consistent with other duration displays (e.g., ACHT)
};

export const getContactStatusI18nKey = (contactStatusValue: string): string => {
  return contactStatus2Key[contactStatusValue.toLowerCase()] ?? NOT_FOUND_KEY;
};
